'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import type { RegistrationActivity } from '@/features/registration';

type Translator = (
  key: string,
  values?: Record<string, string | number>
) => string;

interface ActivityItemProps {
  activity: RegistrationActivity;
  isLast: boolean;
  /** When set (> 1), this row represents a collapsed run of `count` consecutive
   * same-action activities — the label gets a "×count" suffix. */
  count?: number;
}

interface ActivityConfig {
  color: string;
  label: string;
  description: string;
}

const REPAIR_COLOR = 'bg-rose-500';

// Fixed action → config lookup. Repair + refuse events use the standout rose
// color because the activity log is now the home for repair events.
function staticActionConfig(t: Translator): Record<string, ActivityConfig> {
  return {
    // ── Registration ────────────────────────────────────────────────────────
    'registration.created': {
      color: 'bg-blue-500',
      label: t('activityCreated'),
      description: t('activityCreatedDesc')
    },
    'registration.updated': {
      color: 'bg-amber-500',
      label: t('activityUpdated'),
      description: t('activityUpdatedDesc')
    },
    'registration.deleted': {
      color: 'bg-gray-400',
      label: t('activityDeleted'),
      description: t('activityDeletedDesc')
    },
    'registration.images_uploaded': {
      color: 'bg-purple-500',
      label: t('activityImagesUploaded'),
      description: t('activityImagesUploadedDesc')
    },
    'registration.stage_advanced': {
      color: 'bg-green-500',
      label: t('activityStageAdvanced'),
      description: t('activityStageAdvancedDesc')
    },
    'registration.stage_rejected': {
      color: 'bg-red-500',
      label: t('activityStageRejected'),
      description: t('activityStageRejectedDesc')
    },
    'registration.returned_to_registration': {
      color: 'bg-orange-500',
      label: t('activityReturnedToRegistration'),
      description: t('activityReturnedToRegistrationDesc')
    },
    'registration.page_count_set': {
      color: 'bg-slate-500',
      label: t('activityPageCountSet'),
      description: t('activityPageCountSetDesc')
    },
    'registration.sticking_extra_labels_requested': {
      color: 'bg-slate-500',
      label: t('activityStickingExtraLabelsRequested'),
      description: t('activityStickingExtraLabelsRequestedDesc')
    },
    'registration.sticking_actual_count_adjusted': {
      color: 'bg-slate-500',
      label: t('activityStickingActualCountAdjusted'),
      description: t('activityStickingActualCountAdjustedDesc')
    },
    'registration.ocr_templates_synced': {
      color: 'bg-indigo-500',
      label: t('activityOcrTemplatesSynced'),
      description: t('activityOcrTemplatesSyncedDesc')
    },

    // ── Repair (ContainerTransfer) — all standout rose ────────────────────────
    'repairs.refused': {
      color: REPAIR_COLOR,
      label: t('activityRepairsRefused'),
      description: t('activityRepairsRefusedDesc')
    },
    'repairs.start_repair': {
      color: REPAIR_COLOR,
      label: t('activityRepairsStartRepair'),
      description: t('activityRepairsStartRepairDesc')
    },
    'repairs.relabeled': {
      color: REPAIR_COLOR,
      label: t('activityRepairsRelabeled'),
      description: t('activityRepairsRelabeledDesc')
    },
    'repairs.completed': {
      color: REPAIR_COLOR,
      label: t('activityRepairsCompleted'),
      description: t('activityRepairsCompletedDesc')
    },
    'repairs.returned': {
      color: REPAIR_COLOR,
      label: t('activityRepairsReturned'),
      description: t('activityRepairsReturnedDesc')
    },
    'repairs.repair_confirmed': {
      color: REPAIR_COLOR,
      label: t('activityRepairsRepairConfirmed'),
      description: t('activityRepairsRepairConfirmedDesc')
    },
    'repairs.deleted': {
      color: REPAIR_COLOR,
      label: t('activityRepairsDeleted'),
      description: t('activityRepairsDeletedDesc')
    },

    // ── Sorting (Box) ─────────────────────────────────────────────────────────
    'sorting.container_scanned': {
      color: 'bg-cyan-500',
      label: t('activitySortingContainerScanned'),
      description: t('activitySortingContainerScannedDesc')
    },
    'sorting.container_completed': {
      color: 'bg-green-500',
      label: t('activitySortingContainerCompleted'),
      description: t('activitySortingContainerCompletedDesc')
    },
    'sorting.container_refused': {
      color: REPAIR_COLOR,
      label: t('activitySortingContainerRefused'),
      description: t('activitySortingContainerRefusedDesc')
    },

    // ── Box membership ────────────────────────────────────────────────────────
    'box.registration_added': {
      color: 'bg-emerald-500',
      label: t('activityBoxRegistrationAdded'),
      description: t('activityBoxRegistrationAddedDesc')
    },
    'box.registration_removed': {
      color: 'bg-gray-400',
      label: t('activityBoxRegistrationRemoved'),
      description: t('activityBoxRegistrationRemovedDesc')
    },

    // ── Reception ─────────────────────────────────────────────────────────────
    'reception.item_verified': {
      color: 'bg-cyan-500',
      label: t('activityReceptionItemVerified'),
      description: t('activityReceptionItemVerifiedDesc')
    },

    // ── OCR ───────────────────────────────────────────────────────────────────
    'ocr.started': {
      color: 'bg-indigo-500',
      label: t('activityOcrStarted'),
      description: t('activityOcrStartedDesc')
    },
    'ocr.field_updated': {
      color: 'bg-sky-500',
      label: t('activityOcrFieldUpdated'),
      description: t('activityOcrFieldUpdatedDesc')
    },
    'ocr.field_created': {
      color: 'bg-sky-500',
      label: t('activityOcrFieldCreated'),
      description: t('activityOcrFieldCreatedDesc')
    },
    'ocr.field_renamed': {
      color: 'bg-sky-500',
      label: t('activityOcrFieldRenamed'),
      description: t('activityOcrFieldRenamedDesc')
    },
    'ocr.all_accepted': {
      color: 'bg-green-500',
      label: t('activityOcrAllAccepted'),
      description: t('activityOcrAllAcceptedDesc')
    },
    'ocr.submitted_to_qa': {
      color: 'bg-teal-500',
      label: t('activityOcrSubmittedToQa'),
      description: t('activityOcrSubmittedToQaDesc')
    },
    'ocr.retried': {
      color: 'bg-amber-500',
      label: t('activityOcrRetried'),
      description: t('activityOcrRetriedDesc')
    },
    'ocr.batches_retried': {
      color: 'bg-amber-500',
      label: t('activityOcrBatchesRetried'),
      description: t('activityOcrBatchesRetriedDesc')
    },
    'ocr.paused': {
      color: 'bg-amber-500',
      label: t('activityOcrPaused'),
      description: t('activityOcrPausedDesc')
    },
    'ocr.resumed': {
      color: 'bg-indigo-500',
      label: t('activityOcrResumed'),
      description: t('activityOcrResumedDesc')
    },
    'ocr.recovered': {
      color: 'bg-green-500',
      label: t('activityOcrRecovered'),
      description: t('activityOcrRecoveredDesc')
    },
    'ocr.page_accepted': {
      color: 'bg-green-500',
      label: t('activityOcrPageAccepted'),
      description: t('activityOcrPageAcceptedDesc')
    },
    'ocr.page_rerun': {
      color: 'bg-amber-500',
      label: t('activityOcrPageRerun'),
      description: t('activityOcrPageRerunDesc')
    },
    'ocr.pages_rerun': {
      color: 'bg-amber-500',
      label: t('activityOcrPagesRerun'),
      description: t('activityOcrPagesRerunDesc')
    },
    'ocr.page_suggestions_generated': {
      color: 'bg-violet-500',
      label: t('activityOcrPageSuggestionsGenerated'),
      description: t('activityOcrPageSuggestionsGeneratedDesc')
    },
    'ocr.page_suggestion_generated': {
      color: 'bg-violet-500',
      label: t('activityOcrPageSuggestionGenerated'),
      description: t('activityOcrPageSuggestionGeneratedDesc')
    },
    'ocr.chunk_bbox_updated': {
      color: 'bg-sky-500',
      label: t('activityOcrChunkBboxUpdated'),
      description: t('activityOcrChunkBboxUpdatedDesc')
    },
    'ocr.field_highlight_created': {
      color: 'bg-sky-500',
      label: t('activityOcrFieldHighlightCreated'),
      description: t('activityOcrFieldHighlightCreatedDesc')
    },
    'ocr.field_highlight_removed': {
      color: 'bg-rose-500',
      label: t('activityOcrFieldHighlightRemoved'),
      description: t('activityOcrFieldHighlightRemovedDesc')
    },
    'ocr.test_container_created': {
      color: 'bg-slate-500',
      label: t('activityOcrTestContainerCreated'),
      description: t('activityOcrTestContainerCreatedDesc')
    },
    'ocr.test_container_pages_added': {
      color: 'bg-slate-500',
      label: t('activityOcrTestContainerPagesAdded'),
      description: t('activityOcrTestContainerPagesAddedDesc')
    },
    'ocr.test_container_deleted': {
      color: 'bg-rose-500',
      label: t('activityOcrTestContainerDeleted'),
      description: t('activityOcrTestContainerDeletedDesc')
    },

    // ── Photo scanning ──────────────────────────────────────────────────────
    'batch.started': {
      color: 'bg-sky-500',
      label: t('activityBatchStarted'),
      description: t('activityBatchStartedDesc')
    },
    'batch.completed': {
      color: 'bg-green-500',
      label: t('activityBatchCompleted'),
      description: t('activityBatchCompletedDesc')
    },
    'page.scanned': {
      color: 'bg-slate-500',
      label: t('activityPageScanned'),
      description: t('activityPageScannedDesc')
    },
    'pages.bulk_scanned': {
      color: 'bg-slate-500',
      label: t('activityPagesBulkScanned'),
      description: t('activityPagesBulkScannedDesc')
    },
    'page.rotated': {
      color: 'bg-slate-500',
      label: t('activityPageRotated'),
      description: t('activityPageRotatedDesc')
    },
    'page.enhanced': {
      color: 'bg-slate-500',
      label: t('activityPageEnhanced'),
      description: t('activityPageEnhancedDesc')
    },
    'page.deleted': {
      color: 'bg-gray-400',
      label: t('activityPageDeleted'),
      description: t('activityPageDeletedDesc')
    },
    'pages.reordered': {
      color: 'bg-slate-500',
      label: t('activityPagesReordered'),
      description: t('activityPagesReorderedDesc')
    },

    // ── QR review ─────────────────────────────────────────────────────────────
    'qr_review.session_rejected': {
      color: 'bg-red-500',
      label: t('activityQrReviewSessionRejected'),
      description: t('activityQrReviewSessionRejectedDesc')
    },
    'qr_review.page.qr_rescanned': {
      color: 'bg-slate-500',
      label: t('activityQrReviewPageRescanned'),
      description: t('activityQrReviewPageRescannedDesc')
    },

    // ── Handover workload ─────────────────────────────────────────────────────
    'workload_assignment.created': {
      color: 'bg-sky-500',
      label: t('activityWorkloadAssignmentCreated'),
      description: t('activityWorkloadAssignmentCreatedDesc')
    },
    'workload_assignment.self_assigned': {
      color: 'bg-sky-500',
      label: t('activityWorkloadAssignmentSelfAssigned'),
      description: t('activityWorkloadAssignmentSelfAssignedDesc')
    },
    'workload_assignment.updated': {
      color: 'bg-amber-500',
      label: t('activityWorkloadAssignmentUpdated'),
      description: t('activityWorkloadAssignmentUpdatedDesc')
    },
    'workload_assignment.deleted': {
      color: 'bg-gray-400',
      label: t('activityWorkloadAssignmentDeleted'),
      description: t('activityWorkloadAssignmentDeletedDesc')
    },

    // ── Warehouse ─────────────────────────────────────────────────────────────
    'warehouse.assignment.released': {
      color: 'bg-amber-500',
      label: t('activityWarehouseAssignmentReleased'),
      description: t('activityWarehouseAssignmentReleasedDesc')
    },
    'warehouse.assignment.transferred': {
      color: 'bg-sky-500',
      label: t('activityWarehouseAssignmentTransferred'),
      description: t('activityWarehouseAssignmentTransferredDesc')
    },
    'warehouse.assignment.created': {
      color: 'bg-emerald-500',
      label: t('activityWarehouseAssignmentCreated'),
      description: t('activityWarehouseAssignmentCreatedDesc')
    }
  };
}

// Tiered QA actions (`ocr_qa.tier{N}.{verb}`) share ONE base key per verb and
// interpolate the tier number, so we don't write 12 near-identical entries.
const QA_VERB_CONFIG: Record<
  string,
  { color: string; labelKey: string; descKey: string }
> = {
  started: {
    color: 'bg-teal-500',
    labelKey: 'activityQaStarted',
    descKey: 'activityQaStartedDesc'
  },
  verdict: {
    color: 'bg-violet-500',
    labelKey: 'activityQaVerdict',
    descKey: 'activityQaVerdictDesc'
  },
  assigned: {
    color: 'bg-teal-500',
    labelKey: 'activityQaAssigned',
    descKey: 'activityQaAssignedDesc'
  },
  unassigned: {
    color: 'bg-violet-500',
    labelKey: 'activityQaUnassigned',
    descKey: 'activityQaUnassignedDesc'
  }
};

const QA_ACTION_RE =
  /^ocr_qa\.tier(\d)\.(started|verdict|assigned|unassigned)$/;

function resolveConfig(action: string, t: Translator): ActivityConfig {
  const qaMatch = action.match(QA_ACTION_RE);
  if (qaMatch) {
    const tier = Number(qaMatch[1]);
    const verb = QA_VERB_CONFIG[qaMatch[2]];
    return {
      color: verb.color,
      label: t(verb.labelKey, { tier }),
      description: t(verb.descKey, { tier })
    };
  }

  return (
    staticActionConfig(t)[action] ?? {
      color: 'bg-gray-400',
      label: action,
      description: ''
    }
  );
}

export function ActivityItem({ activity, isLast, count }: ActivityItemProps) {
  // The activity strings live in the `container` namespace, which is the only
  // one carrying the full action catalogue. Resolving them here instead of
  // taking a `t` prop stops a caller from passing a namespace that is missing
  // most of the keys (the review page used to, and crashed on the first gap).
  const t = useTranslations('container');

  const stageLabelsMap: Record<string, string> = {
    registration: t('stageRegistration'),
    sticking: t('stageSticking'),
    review: t('stageReview'),
    ready_for_packing: t('stageReadyForPacking'),
    reception: t('timelineReception'),
    sorting: t('timelineSorting'),
    handover_scanning: t('timelineHandoverScanning'),
    photo_scanning: t('timelinePhotoScanning'),
    qr_review: t('timelineQrReview'),
    ocr: t('timelineOcr'),
    qa1_ocr: t('timelineQa1Ocr'),
    qa2_ocr: t('timelineQa2Ocr'),
    qa3_ocr: t('timelineQa3Ocr'),
    warehouse: t('timelineWarehouse'),
    archived: t('timelineArchived'),
    completed: t('stageCompleted')
  };

  const config = resolveConfig(activity.action, t);
  const isRejection = activity.action === 'registration.stage_rejected';

  const meta = activity.meta as {
    rejection_reason?: string;
    to_stage?: string;
    from_stage?: string;
    destination?: string;
    reason?: string;
    comment?: string;
    verdict?: string;
  } | null;

  // Build the extra detail rows from whichever meta keys are present on this
  // action. Rejection rows keep their original red emphasis; the rest are muted.
  const metaRows: {
    key: string;
    label: string;
    value: string;
    emphasis?: boolean;
  }[] = [];
  if (meta) {
    if (isRejection && meta.to_stage) {
      metaRows.push({
        key: 'to_stage',
        label: t('activityReturnedTo'),
        value: stageLabelsMap[meta.to_stage] ?? meta.to_stage,
        emphasis: true
      });
    }
    if (isRejection && meta.rejection_reason) {
      metaRows.push({
        key: 'rejection_reason',
        label: t('activityRejectionReason'),
        value: meta.rejection_reason,
        emphasis: true
      });
    }
    if (meta.destination) {
      metaRows.push({
        key: 'destination',
        label: t('activityDestination'),
        value: meta.destination
      });
    }
    if (meta.reason) {
      metaRows.push({
        key: 'reason',
        label: t('activityReason'),
        value: meta.reason
      });
    }
    if (meta.comment) {
      metaRows.push({
        key: 'comment',
        label: t('activityComment'),
        value: meta.comment
      });
    }
    if (meta.verdict) {
      metaRows.push({
        key: 'verdict',
        label: t('activityVerdict'),
        value: meta.verdict
      });
    }
  }

  const date = new Date(activity.created_at);
  const dayStart = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round(
    (dayStart(new Date()) - dayStart(date)) / (24 * 60 * 60 * 1000)
  );
  const timeOfDay = date.toLocaleTimeString('ar-SA', {
    hour: '2-digit',
    minute: '2-digit'
  });
  const pad = (n: number) => String(n).padStart(2, '0');
  const dateStr = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  const timeStr =
    dayDiff === 0
      ? `${t('today')} ${dateStr}، ${timeOfDay}`
      : dayDiff === 1
        ? `${t('yesterday')} ${dateStr}، ${timeOfDay}`
        : `${dateStr}، ${timeOfDay}`;

  return (
    <div className='relative flex gap-3 pb-6 last:pb-0'>
      {!isLast && (
        <div className='absolute start-[7px] top-5 bottom-0 w-px bg-gray-200 dark:bg-gray-700' />
      )}
      <div
        className={cn('mt-1.5 h-3.5 w-3.5 shrink-0 rounded-full', config.color)}
      />
      <div className='flex-1'>
        <p className='text-sm font-bold'>
          {config.label}
          {count && count > 1 && (
            <span className='text-muted-foreground font-normal'> ×{count}</span>
          )}
        </p>
        {config.description && (
          <p className='text-muted-foreground text-xs'>{config.description}</p>
        )}
        {metaRows.length > 0 && (
          <div className='mt-1 space-y-0.5'>
            {metaRows.map((row) => (
              <p
                key={row.key}
                className={cn(
                  'text-xs',
                  row.emphasis
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-muted-foreground'
                )}
              >
                {row.label}: {row.value}
              </p>
            ))}
          </div>
        )}
        <p className='text-muted-foreground mt-0.5 text-xs'>
          {activity.user?.name && (
            <span className='font-medium'>{activity.user.name} · </span>
          )}
          {timeStr}
        </p>
      </div>
    </div>
  );
}
